import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import { gameState } from '@/state/store';
import { Kolonne } from '@/ui/layout';
import type { BakgrunnsOppfoering } from '@/systems/bakgrunnsMotor';

const SHEET_WIDTH = 980;
const SHEET_HEIGHT = 980;
const PADDING = 40;

/**
 * Dagsoppsummering (fase 1+4, Mad TV-mekanikk) — vises når GameClock
 * når 17:00. Fase 4: lister bakgrunnshendelsene som skjedde mens
 * spilleren var opptatt andre steder. «Start ny dag» trigger
 * gameState.nesteDag() via callback fra ForlagshusetScene.
 */
export class DagsoppsummeringModal {
  private layer?: Phaser.GameObjects.Container;

  constructor(private scene: Phaser.Scene) {}

  get isOpen(): boolean {
    return !!this.layer;
  }

  open(onNyDag: () => void, hendelser: readonly BakgrunnsOppfoering[] = []): void {
    if (this.layer) return;

    // Depth 600 — over alle andre modaler (NavnInputModal ligger på 400),
    // siden dagslutt kan inntreffe mens en annen modal er åpen.
    this.layer = this.scene.add.container(0, 0).setDepth(600);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.82)
      .setOrigin(0, 0)
      .setInteractive();
    this.layer.add(backdrop);

    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheetY = (GAME_HEIGHT - SHEET_HEIGHT) / 2;

    const ramme = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_WIDTH, SHEET_HEIGHT, COLORS.wine, 1)
      .setOrigin(0, 0);
    this.layer.add(ramme);

    const innerPad = 14;
    const sheet = this.scene.add
      .rectangle(
        sheetX + innerPad,
        sheetY + innerPad,
        SHEET_WIDTH - innerPad * 2,
        SHEET_HEIGHT - innerPad * 2,
        COLORS.beige,
        1,
      )
      .setOrigin(0, 0);
    this.layer.add(sheet);

    const contentX = sheetX + PADDING;
    const contentW = SHEET_WIDTH - PADDING * 2;
    const kol = new Kolonne(contentX, sheetY + PADDING + 8);

    const ikon = this.scene.add
      .text(contentX, 0, '🌙', {
        fontFamily: FONTS.display,
        fontSize: fontStr(72),
        color: '#862736',
      })
      .setOrigin(0, 0);
    kol.legg(ikon, 0);
    this.layer.add(ikon);

    const header = this.scene.add
      .text(contentX, 0, 'Dagen er omme', {
        fontFamily: FONTS.display,
        fontSize: fontStr(56),
        color: '#862736',
        wordWrap: { width: contentW },
      })
      .setOrigin(0, 0);
    kol.legg(header, 24);
    this.layer.add(header);

    const body = this.scene.add
      .text(
        contentX,
        0,
        `Klokka er 17:00 og Forlagshuset stenger for dag ${gameState.dag}.`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(28),
          color: '#1a1a1a',
          wordWrap: { width: contentW },
          lineSpacing: 8,
        },
      )
      .setOrigin(0, 0);
    kol.legg(body, 28);
    this.layer.add(body);

    // Fase 4: hva skjedde mens du var opptatt et annet sted?
    const overskrift = this.scene.add
      .text(
        contentX,
        0,
        hendelser.length > 0 ? 'MENS DU VAR OPPTATT' : 'En stille dag i bygget.',
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(16),
          color: '#8b5e3c',
          fontStyle: '700',
        },
      )
      .setOrigin(0, 0);
    kol.legg(overskrift, 30);
    this.layer.add(overskrift);

    for (const h of hendelser.slice(0, 5)) {
      const linje = this.scene.add
        .text(contentX, 0, `•  ${h.oppsummering}`, {
          fontFamily: FONTS.body,
          fontSize: fontStr(23),
          color: '#1a1a1a',
          wordWrap: { width: contentW },
          lineSpacing: 4,
        })
        .setOrigin(0, 0);
      kol.legg(linje, 14);
      this.layer.add(linje);
    }

    const knappH = 96;
    const knappY = sheetY + SHEET_HEIGHT - PADDING - knappH;
    const knappBg = this.scene.add
      .rectangle(contentX, knappY, contentW, knappH, COLORS.wine, 1)
      .setOrigin(0, 0);
    const knappTxt = this.scene.add
      .text(contentX + contentW / 2, knappY + knappH / 2, 'Start ny dag →', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(34),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    this.layer.add([knappBg, knappTxt]);

    knappBg.setInteractive({ useHandCursor: true });
    knappBg.on('pointerup', () => {
      this.close();
      onNyDag();
    });
  }

  close(): void {
    this.layer?.destroy(true);
    this.layer = undefined;
  }
}
